import { createContext, use, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { I18nBundle, Lang } from '@/api/types';

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  bundle: I18nBundle | null;
  isLoading: boolean;
  /** Resolve a UI key with a default fallback. */
  t: (key: string, fallback?: string) => string;
  /** Resolve a planet/karaka/etc. id to a display label in the active language. */
  rashi: (rashiNum: number) => string;
}

const STORAGE_KEY = 'astro.lang';

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const initial = (typeof window !== 'undefined'
    && (localStorage.getItem(STORAGE_KEY) as Lang | null)) || 'hi';

  const [lang, setLangRaw] = useState<Lang>(initial);

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const { data: bundle, isLoading } = useQuery({
    queryKey: ['i18n', lang],
    queryFn: () => api.i18n(lang),
    staleTime: 24 * 60 * 60_000, // 24h
  });

  const value = useMemo<LangContextValue>(() => ({
    lang,
    setLang: setLangRaw,
    bundle: bundle ?? null,
    isLoading,
    t: (key, fallback) => bundle?.ui[key] ?? fallback ?? key,
    rashi: (n) => bundle?.rashis[n - 1] ?? `R${n}`,
  }), [lang, bundle, isLoading]);

  return <LangContext value={value}>{children}</LangContext>;
}

export function useLang(): LangContextValue {
  const ctx = use(LangContext);
  if (!ctx) {
    throw new Error('useLang must be used inside <LangProvider>');
  }
  return ctx;
}
